import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Overflow (`···`) menu for a table row or card.
 *
 * The panel is rendered in a portal on `document.body` and positioned with
 * `position: fixed` against the trigger's viewport rect. That is load-bearing:
 * list panels clip their children (`overflow-hidden` for the rounded corners,
 * `overflow-x-auto` for wide tables), so an absolutely positioned menu inside
 * the panel is cut off — invisible for the last row, and unreachable on phones.
 *
 * Open state stays with the parent so a list can keep a single menu open.
 */

export interface MenuAction {
  label: string;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

const MENU_WIDTH = 176;
/** Gap between the trigger and the panel, and the minimum viewport margin. */
const GAP = 6;
const EDGE = 8;
/** Height guess used for the first paint, before the panel can be measured. */
const ITEM_HEIGHT = 34;

interface Position {
  top: number;
  left: number;
}

function place(rect: DOMRect, height: number): Position {
  const maxLeft = Math.max(EDGE, window.innerWidth - MENU_WIDTH - EDGE);
  const left = Math.min(Math.max(EDGE, rect.right - MENU_WIDTH), maxLeft);
  const below = rect.bottom + GAP;
  const fitsBelow = below + height <= window.innerHeight - EDGE;
  const top = fitsBelow ? below : Math.max(EDGE, rect.top - GAP - height);
  return { top, left };
}

export function RowMenu({
  isOpen,
  onToggle,
  actions,
  label = 'Row actions',
}: {
  isOpen: boolean;
  onToggle: (open: boolean) => void;
  actions: MenuAction[];
  label?: string;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position | null>(null);

  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const height = panelRef.current?.offsetHeight ?? actions.length * ITEM_HEIGHT + 12;
    setPosition(place(trigger.getBoundingClientRect(), height));
  }, [actions.length]);

  useLayoutEffect(() => {
    if (isOpen) reposition();
    else setPosition(null);
  }, [isOpen, reposition]);

  useEffect(() => {
    if (!isOpen) return;
    const close = () => {
      onToggle(false);
      triggerRef.current?.focus();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    // Capture phase so the menu follows its row inside scrolling panels too.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onToggle, reposition]);

  return (
    <div className="flex justify-end" onClick={(e) => e.stopPropagation()} role="presentation">
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => onToggle(!isOpen)}
        className={`touch-target flex size-9 items-center justify-center rounded-lg transition-colors hover:bg-(--color-surface-sunken) hover:text-(--color-ink-900) ${
          isOpen ? 'bg-(--color-surface-sunken) text-(--color-ink-900)' : 'text-(--color-ink-500)'
        }`}
      >
        {/* Drawn rather than typed: the `···` glyph rendered as a faint dash in
            the UI face, and a 36px box is a real touch target. */}
        <svg viewBox="0 0 20 20" className="size-[18px]" aria-hidden fill="currentColor">
          <circle cx="4.5" cy="10" r="1.5" />
          <circle cx="10" cy="10" r="1.5" />
          <circle cx="15.5" cy="10" r="1.5" />
        </svg>
      </button>
      {isOpen
        ? createPortal(
            <div onClick={(e) => e.stopPropagation()} role="presentation">
              <div className="fixed inset-0 z-40" role="presentation" onClick={() => onToggle(false)} />
              <div
                ref={panelRef}
                role="menu"
                style={{ top: position?.top ?? 0, left: position?.left ?? 0, width: MENU_WIDTH }}
                className={`fixed z-50 overflow-hidden rounded-xl bg-(--color-surface) py-1.5 shadow-[0_16px_40px_-16px_rgba(27,23,48,0.35)] ring-1 ring-(--color-line) ${
                  position ? '' : 'invisible'
                }`}
              >
                {actions.map((action) => (
                  <button
                    key={action.label}
                    type="button"
                    role="menuitem"
                    disabled={action.disabled}
                    onClick={() => {
                      onToggle(false);
                      action.onSelect();
                    }}
                    className={`block w-full px-3.5 py-2 text-left text-[13px] font-medium transition-colors disabled:opacity-50 ${
                      action.danger
                        ? 'text-(--color-danger-text) hover:bg-(--color-danger-wash)'
                        : 'text-(--color-ink-700) hover:bg-(--color-accent-wash-soft) hover:text-(--color-ink-900)'
                    }`}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
